import React, { useState } from "react";
import { FiClock, FiEdit2 } from "react-icons/fi";
import classnames from "classnames";
import Select from "react-select";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Label,
  FormGroup,
  InputGroup,
  InputGroupText,
  Form,
  Input,
} from "reactstrap";
import { ToastContainer } from "react-toastify";
import SimpleHeader from "@/components/Headers/SimpleHeader";
import ImageUploadContainer from "../User/adding-user/ImageUploadContainer";
import LinkedSites from "./LinkedSites";

/** A pending change's per-field diff; `to` is the proposed value, arbitrary JSON per field. */
type PendingDiff = Record<string, { from?: unknown; to?: any }>; // any: proposed value is arbitrary JSON per field, read as string, boolean and image object below

type LockedFieldProps = {
  fieldKey: string;
  diff: PendingDiff | null;
  unlocked: boolean;
  onUnlock: (key: string) => void;
  children: React.ReactNode;
  inline?: boolean;
};

function LockedField({ fieldKey, diff, unlocked, onUnlock, children, inline = false }: LockedFieldProps) {
  const [hovered, setHovered] = useState(false)
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const pending = diff?.[fieldKey]
  if (!pending || unlocked) return <>{children}</>

  const proposed = pending.to == null ? "—"
    : typeof pending.to === "boolean" ? (pending.to ? "Yes" : "No")
    : String(pending.to)
  const show = () => { if (timerRef.current) clearTimeout(timerRef.current); setHovered(true) }
  const hide = () => { timerRef.current = setTimeout(() => setHovered(false), 120) }

  if (inline) {
    return (
      <div style={{ position: "relative", display: "inline-block" }} onMouseEnter={show} onMouseLeave={hide}>
        <div style={{ pointerEvents: "none" }}>{children}</div>
        {hovered && (
          <div
            onMouseEnter={show}
            onMouseLeave={hide}
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              zIndex: 20,
              background: "#fef9c3",
              color: "#713f12",
              border: "1px solid #fde047",
              borderRadius: "6px",
              padding: "8px 14px",
              fontSize: "0.78rem",
              whiteSpace: "nowrap",
              boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <FiClock size={13} style={{ flexShrink: 0 }} />
            <span>Pending review &nbsp;·&nbsp; Proposed: <strong>{proposed}</strong></span>
            <button
              type="button"
              onClick={() => onUnlock(fieldKey)}
              style={{ display: "flex", alignItems: "center", gap: 4, background: "#fde047", border: "1px solid #ca8a04", borderRadius: "4px", color: "#713f12", cursor: "pointer", fontSize: "0.72rem", fontWeight: 600, padding: "3px 10px" }}
            >
              <FiEdit2 size={11} />
              Edit
            </button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={{ position: "relative" }} onMouseEnter={show} onMouseLeave={hide}>
      <div style={{ pointerEvents: "none" }}>
        {children}
      </div>
      <div style={{
        position: "absolute",
        inset: 0,
        border: "2px solid #fde047",
        borderRadius: "0.375rem",
        background: "rgba(254, 249, 195, 0.45)",
        cursor: "not-allowed",
        zIndex: 2,
      }} />
      {hovered && (
        <div
          onMouseEnter={show}
          onMouseLeave={hide}
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 20,
            background: "#fef9c3",
            color: "#713f12",
            border: "1px solid #fde047",
            borderRadius: "6px",
            padding: "8px 14px",
            fontSize: "0.78rem",
            whiteSpace: "nowrap",
            boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <FiClock size={13} style={{ flexShrink: 0 }} />
          <span>Pending review &nbsp;·&nbsp; Proposed: <strong>{proposed}</strong></span>
          <button
            type="button"
            onClick={() => onUnlock(fieldKey)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              background: "#fde047",
              border: "1px solid #ca8a04",
              borderRadius: "4px",
              color: "#713f12",
              cursor: "pointer",
              fontSize: "0.72rem",
              fontWeight: 600,
              padding: "3px 10px",
            }}
          >
            <FiEdit2 size={11} />
            Edit
          </button>
        </div>
      )}
    </div>
  )
}

type SelectOption = { value: string; label: string };

export type UpdateProductViewProps = {
  /** the product document; UpdateProductContainer's state is `any` (translated from JSDoc) */
  product: any; // any: the product document from UpdateProductContainer state; productSlice types product_byId as any (productSlice.ts:48)
  /** passed by the container; this view does not read it */
  currentSite?: unknown;
  isLinkedToCurrent: boolean;
  onToggleLink: (e: React.SyntheticEvent) => void;
  countriesOptions: readonly SelectOption[];
  countryOption: SelectOption | null;
  clientsOptions: SelectOption[];
  selectedClientOpt: SelectOption | null;
  title: string;
  alt_pic: string;
  sponsored: boolean;
  rating: number | string;
  productImgDisplay?: string;
  entityPendingChange: { diff?: PendingDiff } | null;
  onAltChange: React.ChangeEventHandler<HTMLInputElement>;
  onTitleChange: React.ChangeEventHandler<HTMLInputElement>;
  onCountryChange: (option: SelectOption | null) => void;
  onClientChange: (option: SelectOption | null) => void;
  onToggleSponsored: React.ChangeEventHandler<HTMLInputElement>;
  onRatingChange: React.ChangeEventHandler<HTMLInputElement>;
  onReceiveImage: (img: unknown) => void;
  onRemoveImage: () => void;
  onUpdate: (e: React.SyntheticEvent) => void;
};

const UpdateProductView = ({
  // header & context
  product,
  currentSite,
  isLinkedToCurrent,
  onToggleLink,

  // selects
  countriesOptions,
  countryOption,
  clientsOptions,
  selectedClientOpt,

  // values
  title,
  alt_pic,
  sponsored,
  rating,
  productImgDisplay,
  entityPendingChange,

  // handlers
  onAltChange,
  onTitleChange,
  onCountryChange,
  onClientChange,
  onToggleSponsored,
  onRatingChange,
  onReceiveImage,
  onRemoveImage,
  onUpdate,
}: UpdateProductViewProps) => {
  const diff = entityPendingChange?.diff || null
  const [unlockedFields, setUnlockedFields] = useState(new Set())
  const handleUnlock = (key) => setUnlockedFields((prev) => new Set([...prev, key]))
  const unlocked = (key) => !diff || unlockedFields.has(key)

  return (
    <>
      <div className="rna-wrapper">
        <ToastContainer />
      </div>

      <SimpleHeader name="Update Product " parentName="Products " />

      <Container className="mt--6" fluid>
        <Row>
          <Col className="order-xl-1" xl="8">
            <Card>
              <CardHeader style={{ display: "flex", position: "relative" }}>
                <h3 className="mb-0">Update Product</h3>

                {isLinkedToCurrent ? (
                  <Button
                    className="my-0"
                    color="btn btn-outline-danger"
                    onClick={onToggleLink}
                    style={{ right: 5, top: 7, position: "absolute" }}
                  >
                    Unlink from current website
                  </Button>
                ) : (
                  <Button
                    className="my-0"
                    color="btn btn-outline-success"
                    onClick={onToggleLink}
                    style={{ right: 5, top: 7, position: "absolute" }}
                  >
                    Link To Website
                  </Button>
                )}
              </CardHeader>

              <CardBody>
                <Form role="form" onSubmit={(e) => e.preventDefault()}>
                  {/* Alt Picture */}
                  <FormGroup className="row">
                    <Label className="form-control-label" md="2">
                      Alt Picture
                    </Label>
                    <Col md="7">
                      <LockedField fieldKey="alt_pic" diff={diff} unlocked={unlocked("alt_pic")} onUnlock={handleUnlock}>
                        <InputGroup className={classnames("input-group-merge")}>
                          <InputGroupText>
                            <i className="ni ni-album-2" />
                          </InputGroupText>
                          <Input
                            placeholder="Add alternative text to the picture"
                            name="alt_pic"
                            type="text"
                            value={alt_pic}
                            onChange={onAltChange}
                          />
                        </InputGroup>
                      </LockedField>
                    </Col>
                  </FormGroup>

                  {/* Country Code — read-only: products are country-scoped, the
                      country is fixed after creation (SCRUM-754). */}
                  <FormGroup className="row">
                    <Label className="form-control-label" md="2">
                      Country Code
                    </Label>
                    <Col md="7">
                      <LockedField fieldKey="country_code" diff={diff} unlocked={unlocked("country_code")} onUnlock={handleUnlock}>
                        <Select
                          value={
                            countriesOptions.find(
                              (o) =>
                                o.value === (product?.country_code || countryOption?.value)
                            ) || null
                          }
                          options={countriesOptions}
                          className="basic-multi-select"
                          classNamePrefix="select"
                          onChange={onCountryChange}
                          isDisabled={true}
                        />
                      </LockedField>
                    </Col>
                  </FormGroup>

                  {/* Client */}
                  <FormGroup className="row">
                    <Label className="form-control-label" md="2">
                      Client
                    </Label>
                    <Col md="7">
                      <LockedField fieldKey="client" diff={diff} unlocked={unlocked("client")} onUnlock={handleUnlock}>
                        <Select
                          options={clientsOptions}
                          onChange={onClientChange}
                          isClearable
                          isSearchable
                          value={selectedClientOpt || null}
                          isDisabled={!!diff?.client && !unlocked("client")}
                        />
                      </LockedField>
                    </Col>
                  </FormGroup>

                  {/* Title */}
                  <FormGroup className="row">
                    <Label className="form-control-label" md="2">
                      Title
                    </Label>
                    <Col md="7">
                      <LockedField fieldKey="title" diff={diff} unlocked={unlocked("title")} onUnlock={handleUnlock}>
                        <InputGroup className={classnames("input-group-merge")}>
                          <InputGroupText>
                            <i className="ni ni-box-2" />
                          </InputGroupText>
                          <Input
                            placeholder="Insert Product Title"
                            name="title"
                            type="text"
                            value={title}
                            onChange={onTitleChange}
                          />
                        </InputGroup>
                      </LockedField>
                    </Col>
                  </FormGroup>

                  {/* SCRUM-312: the Active toggle was removed here. The flag was
                      never read on the public side — no sitemap, listing or render
                      query filtered on it — so switching a product off changed
                      nothing a visitor could see, which is the editor confusion
                      the ticket describes. The update payload no longer carries
                      the field, so an existing value is left untouched rather
                      than rewritten. A pending change queued BEFORE this removal
                      can still hold an `active` entry: approval applies it
                      server-side as it always did, it is simply no longer shown
                      as a locked field here. */}

                  {/* Sponsored */}
                  <FormGroup className="row">
                    <Label className="form-control-label" md="2">
                      Sponsored
                    </Label>
                    <Col md="7">
                      <LockedField fieldKey="sponsored" diff={diff} unlocked={unlocked("sponsored")} onUnlock={handleUnlock} inline>
                        <label
                          className="custom-toggle custom-toggle-success mr-1"
                          style={diff?.sponsored && !unlocked("sponsored") ? { boxShadow: "0 0 0 2px #fde047", borderRadius: "2rem", opacity: 0.75 } : {}}
                        >
                          <input checked={!!product?.sponsored} onChange={onToggleSponsored} name="sponsored" type="checkbox" disabled={!!diff?.sponsored && !unlocked("sponsored")} />
                          <span className="custom-toggle-slider rounded-circle" data-label-off="No" data-label-on="Yes" />
                        </label>
                      </LockedField>
                    </Col>
                  </FormGroup>

                  {/* Rating */}
                  <FormGroup className="row">
                    <Label className="form-control-label" md="2">
                      Rating <br />
                      <p>{product?.rating ?? 0} /10</p>
                    </Label>
                    <Col md="7">
                      <LockedField fieldKey="rating" diff={diff} unlocked={unlocked("rating")} onUnlock={handleUnlock}>
                        <InputGroup className={classnames("input-group-merge")}>
                          <InputGroupText>
                            <i className="ni ni-box-2" />
                          </InputGroupText>
                          <Input
                            placeholder="Insert Product Rating"
                            name="rating"
                            type="number"
                            step="0.1"
                            min="0"
                            max="10"
                            value={product?.rating ?? 0}
                            onChange={onRatingChange}
                          />
                        </InputGroup>
                      </LockedField>
                    </Col>
                  </FormGroup>
                </Form>

                {/* SCRUM-754: restore the product save trigger that was dropped in
                    the container/view split (e1c9edb). onUpdate persists the edited
                    fields via updateProduct. */}
                <div className="text-right">
                  <Button color="btn btn-outline-primary" onClick={onUpdate}>
                    Update Product
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Image upload */}
          <Col className="order-xl-2" xl="4">
            <Card className="card-pricing border-0 text-center mb-4">
              <CardHeader className="bg-gradient-success">
                <h4 className="text-uppercase ls-0 text-white py-2 mb-0">
                  Update Image
                </h4>
              </CardHeader>
              <CardBody className="px-lg-2">
                <ImageUploadContainer
                  ImageUpload={onReceiveImage}
                  onRemove={onRemoveImage}
                  image={productImgDisplay}
                  pendingImg={diff?.productImg && !unlocked("productImg") ? (diff.productImg.to || null) : undefined}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Linked websites */}
        <h3>Linked Websites</h3>
        <LinkedSites product={product} />
      </Container>
    </>
  );
};

export default UpdateProductView;
